import { BatchId, Bee, EthAddress, FeedIndex, Topic, UploadResult } from "@ethersphere/bee-js";

import { isLegacyUserComment, isUserComment } from "../asserts";
import { DEFAULT_BEE_URL } from "../constants/constants";
import { MessageData, MessageType } from "../model";
import { UserComment } from "../model/legacy.model";
import { Options } from "../model/options.model";
import { Optional } from "../model/util.types";

import { IdentifierError, StampError } from "./errors";
import { getUsableStamp } from "./stamps";
import { FeedData } from "./types";
import { getIdentifierFromUrl } from "./url";

async function prepareOptions(
  options: Options = {},
  stampRequired = true,
): Promise<Optional<Required<Options>, "stamp" | "signer" | "address">> {
  const beeApiUrl = options.beeApiUrl ?? DEFAULT_BEE_URL;
  const { signer, address } = options;
  let { identifier, stamp } = options;

  if (!identifier) {
    identifier = getIdentifierFromUrl(window.location.href);
  }

  if (!identifier) {
    throw new IdentifierError("Cannot generate private key from an invalid URL");
  }

  if (!stamp && stampRequired) {
    const usableStamp = await getUsableStamp(beeApiUrl);

    if (!usableStamp) {
      throw new StampError("No available stamps found.");
    }

    stamp = usableStamp.batchID;
  }

  return {
    stamp,
    identifier,
    beeApiUrl,
    signer,
    address,
  };
}

export function prepareWriteOptions(options: Options = {}): Promise<Optional<Required<Options>, "signer">> {
  return prepareOptions(options) as Promise<Optional<Required<Options>, "signer">>;
}

export function prepareReadOptions(
  options: Options = {},
): Promise<Omit<Optional<Required<Options>, "address">, "stamp" | "signer">> {
  return prepareOptions(options, false);
}

export async function readFeedData(
  bee: Bee,
  identifier: string | Uint8Array,
  address: string | EthAddress,
  index?: FeedIndex,
): Promise<FeedData> {
  const feedReader = bee.makeFeedReader(identifier, address);

  const feedUpdate = await feedReader.downloadReference(index ? { index } : undefined);
  const nextIndex =
    feedUpdate.feedIndexNext !== undefined
      ? feedUpdate.feedIndexNext.toBigInt()
      : feedUpdate.feedIndex.next().toBigInt();
  const data = await bee.downloadData(feedUpdate.reference.toUint8Array());

  return { data: data.toJSON(), nextIndex };
}

export async function writeFeedData(
  bee: Bee,
  topic: Topic | Uint8Array | string,
  stamp: string | BatchId,
  signer: Uint8Array | string,
  data: string | Uint8Array,
  index?: FeedIndex,
): Promise<UploadResult> {
  const { reference } = await bee.uploadData(stamp, data);
  const feedWriter = bee.makeFeedWriter(topic, signer);
  return await feedWriter.uploadReference(stamp, reference.toUint8Array(), index === undefined ? undefined : { index });
}

export function isNotFoundError(error: any): boolean {
  return (
    error.stack.includes("404") ||
    error.message.includes("Not Found") ||
    error.message.includes("404") ||
    error.code === 404
  );
}

export function transformLegacyComment(
  obj: UserComment,
  derivedAddress: string,
  index: string,
  topic: string,
): MessageData {
  const { username, message, timestamp, address } = obj;
  const { text, messageId, threadId, flagged, reason } = message;

  const transformed: MessageData = {
    id: messageId,
    username,
    timestamp,
    type: MessageType.TEXT,
    message: text,
    address: address || derivedAddress,
    index,
    topic,
    targetMessageId: threadId,
    signature: undefined,
    flagged,
    reason,
    isLegacy: true,
  };

  return transformed;
}

export function assertAndTransformData(
  data: unknown,
  address: string,
  index: FeedIndex,
  identifier: string,
): MessageData {
  if (isUserComment(data)) {
    return data;
  }
  if (isLegacyUserComment(data)) {
    return transformLegacyComment(data, address, index.toString(), identifier);
  }

  throw new TypeError(`Invalid comment format: ${JSON.stringify(data)}`);
}
